<template>
  <div class="kjp-table-wrap" :style="cssVars">
    <div v-if="model.warnings.length && !dismissed"
      class="alert alert-warning alert-dismissible py-2 px-3 small mb-2">
      <button type="button" class="close" aria-label="Close" @click="dismissed = true">
        <span aria-hidden="true">&times;</span>
      </button>
      <div v-for="(w, i) in warningMessages" :key="i">{{ w }}</div>
    </div>
    <div v-if="hasHiddenMonths" class="d-flex justify-content-end mb-1">
      <button type="button" class="btn btn-outline-light btn-sm py-0 px-2 kjp-month-toggle"
        @click="showAllMonths = !showAllMonths">
        {{ showAllMonths ? "Show contract months only" : "Show all 12 months" }}
      </button>
    </div>
    <div class="table-responsive">
      <table class="table table-sm table-bordered mb-0 kjp-table">
        <thead>
          <tr>
            <th v-for="col in leadingColumns" :key="col.type" rowspan="2">{{ col.label }}</th>
            <th :colspan="visibleMonths.length">{{ year }}</th>
            <!-- Labels come from exportLayout.js so the screen and the
                 exported sheet cannot word these differently. Each carries its
                 unit on a second line, as the workbook does. -->
            <th v-for="(label, i) in rollupLabels" :key="label"
              rowspan="2" class="kjp-th-summary" :class="{ 'kjp-th-baki': i % 2 === 1 }">{{ label }}</th>
          </tr>
          <tr>
            <th v-for="m in visibleMonths" :key="m.index">{{ m.label }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="row in model.rows">
            <tr v-for="(sub, i) in row.subRows" :key="`${row.districtId}-${i}`"
              :class="{ 'font-weight-bold': sub.kind === 'ceiling' || sub.kind === 'pct' }">
              <template v-for="mc in mergedColumns">
                <td v-if="i === 0" :key="mc.type" :rowspan="row.subRows.length"
                  :class="mc.type === 'district' ? 'font-weight-bold align-middle' : 'align-middle text-right'">
                  {{ mc.type === 'district' ? row.districtName : (row.contractKm ? fmt(row.contractKm, 3) : '—') }}
                </td>
              </template>
              <td v-for="pc in perRowColumns" :key="pc.type" :class="{ 'text-right': pc.type === 'quota' }">
                {{ pc.type === 'label' ? sub.label : quotaCell(sub) }}
              </td>
              <td v-for="vm in visibleMonths" :key="vm.index" class="text-right kjp-month-cell"
                :class="{ 'kjp-cell-unmeasured': sub.cells[vm.colIndex].unmeasured }">
                {{ cell(sub, sub.cells[vm.colIndex]) }}
              </td>
              <td class="text-right">{{ rollup(sub, sub.usedToDate, sub.unmeasuredToDate) }}</td>
              <td class="text-right">{{ rollup(sub, sub.bakiToDate, sub.unmeasuredToDate) }}</td>
              <td class="text-right">{{ rollup(sub, sub.usedAnnual, sub.unmeasuredAnnual) }}</td>
              <td class="text-right">{{ rollup(sub, sub.bakiAnnual, sub.unmeasuredAnnual) }}</td>
            </tr>
          </template>
          <tr v-for="(sub, i) in model.totals" :key="`total-${i}`" class="kjp-total">
            <td v-if="i === 0" :rowspan="model.totals.length" :colspan="mergedColumns.length"
              class="font-weight-bold align-middle">
              JUMLAH KESELURUHAN
            </td>
            <td v-for="pc in perRowColumns" :key="pc.type" :class="{ 'text-right': pc.type === 'quota' }">
              {{ pc.type === 'label' ? sub.label : quotaCell(sub) }}
            </td>
            <td v-for="vm in visibleMonths" :key="vm.index" class="text-right kjp-total-month-cell"
              :class="{ 'kjp-cell-unmeasured': sub.cells[vm.colIndex].unmeasured }">
              {{ cell(sub, sub.cells[vm.colIndex]) }}
            </td>
            <td class="text-right">{{ rollup(sub, sub.usedToDate, sub.unmeasuredToDate) }}</td>
            <td class="text-right">{{ rollup(sub, sub.bakiToDate, sub.unmeasuredToDate) }}</td>
            <td class="text-right">{{ rollup(sub, sub.usedAnnual, sub.unmeasuredAnnual) }}</td>
            <td class="text-right">{{ rollup(sub, sub.bakiAnnual, sub.unmeasuredAnnual) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { round2 } from "@/kjp";
import { KJP_CSS_COLORS } from "@/components/kjp/colors";
import { KJP_LEADING_COLUMNS, mergedLeadingColumns, perRowLeadingColumns } from "@/components/kjp/columns";
import dismissableNotice from "@/components/kjp/dismissableNotice";
import { rollupHeaders } from "@/components/kjp/exportLayout";

export default {
  name: "kjp-table",
  mixins: [dismissableNotice],
  props: ["model", "year", "startMonth", "monthCount"],
  data() {
    return {
      // Contract-window view by default (INFS-1281): the twelve calendar
      // months must stay in the model (see kjp.js) so the annual roll-up
      // keeps including work recorded before the contract started, but a
      // reader should not have to stare at eight columns of the contract's
      // own quota table reading blank/zero for months it never covered.
      // This toggle only ever changes which columns render — every figure
      // behind it, hidden or shown, is the same model data.
      showAllMonths: false,
    };
  },
  computed: {
    // Leading-column layout differs per scope (INFS-1281 Defect C) — read
    // from columns.js rather than hardcoded here, so this component never
    // branches on model.scope itself.
    // The four roll-up headers, scope's unit included. Shared with the Excel
    // export (exportLayout.js) rather than spelled out in this template, so a
    // reader comparing the page against the downloaded sheet sees the same
    // wording in both. Their order is fixed — to-date pair, then annual pair —
    // and the odd indices are the two "baki" columns the workbook highlights.
    rollupLabels() {
      return rollupHeaders(this.model.scope, this.model.asAtLabel);
    },
    leadingColumns() {
      return KJP_LEADING_COLUMNS[this.model.scope] || KJP_LEADING_COLUMNS.R01;
    },
    // The columns that merge down a district's (or JUMLAH KESELURUHAN's)
    // sub-rows — district name plus, for R01 only, the contract-length total.
    mergedColumns() {
      return mergedLeadingColumns(this.model.scope);
    },
    // The remaining leading columns (MAKLUMAT, quota), rendered once per
    // sub-row.
    perRowColumns() {
      return perRowLeadingColumns(this.model.scope);
    },
    lastContractMonth() {
      return (this.startMonth || 1) + (this.monthCount || 12) - 1;
    },
    // No column is actually hidden for a full Jan-Dec, 12-month contract, so
    // the toggle itself is withheld rather than offered as a no-op.
    hasHiddenMonths() {
      return (this.monthCount || 12) < 12;
    },
    // model.months is always the full twelve calendar months (see kjp.js);
    // this narrows what's *displayed* to the contract window unless the
    // reader has asked to see all twelve. `colIndex` keeps each visible
    // month's original position in `sub.cells`, which stays a full
    // twelve-length array regardless of what's shown here.
    visibleMonths() {
      return this.model.months
        .map((m, colIndex) => ({ ...m, colIndex }))
        .filter((m) => this.showAllMonths ||
          (m.index >= (this.startMonth || 1) && m.index <= this.lastContractMonth));
    },
    // Bound onto the wrapper as CSS custom properties so the scoped
    // stylesheet below can use the workbook palette without hardcoding hex a
    // second time — colors.js stays the one source both the screen and the
    // export read from.
    cssVars() {
      return {
        "--kjp-header-fill": KJP_CSS_COLORS.headerFill,
        "--kjp-header-baki-fill": KJP_CSS_COLORS.headerBakiFill,
        "--kjp-month-fill": KJP_CSS_COLORS.monthFill,
        "--kjp-total-month-fill": KJP_CSS_COLORS.totalMonthFill,
        "--kjp-text": KJP_CSS_COLORS.text,
      };
    },
    warningMessages() {
      const msgs = [];

      const noLength = this.model.warnings.filter((w) => w.code === "NO_CONTRACT_LENGTH");
      if (noLength.length) {
        msgs.push(
          `Contract length not set for: ${noLength.map((w) => w.district).join(", ")}. ` +
            `Quotas for these districts cannot be calculated.`
        );
      }

      const noMeas = this.model.warnings.filter((w) => w.code === "NO_MEASUREMENTS");
      if (noMeas.length) {
        msgs.push(
          `No dimensions recorded for: ${noMeas.map((w) => w.district).join(", ")}. ` +
            `Their usage is shown as not measured, not as zero.`
        );
      }

      if (this.model.scope === "R01") {
        msgs.push(
          `Cases recorded before the Heavy Patching work type existed are counted ` +
            `under Pothole (1x1m). The workbook shows that volume under Heavy Patching.`
        );
      }

      return msgs;
    },
  },
  methods: {
    // round2 before formatting so the screen matches the workbook — plain
    // toLocaleString would show 5,034.22 where the sheet shows 5,034.23.
    fmt(v, dp = 2) {
      const n = dp === 2 ? round2(v) : Number(v);

      return n.toLocaleString("en-MY", {
        minimumFractionDigits: dp,
        maximumFractionDigits: dp,
      });
    },
    // The quota column's own precision depends on what it's showing: a
    // 'class' row's quota is a contract length (Defect B), displayed at 3dp
    // like the KONTRAK (KM) column above — e.g. Gombak's 64.605, not a
    // rounded 64.61 — while every other quota (ceiling, R01's split types)
    // stays at 2dp.
    quotaCell(sub) {
      if (sub.quota == null) return "";

      return this.fmt(sub.quota, sub.kind === "class" ? 3 : 2);
    },
    // Unmeasured never renders as a number — a 0.00 here reads as "no work
    // done" when it actually means "nobody filled the form in".
    cell(sub, c) {
      if (c.unmeasured) return "—";
      if (sub.kind === "count") return this.fmt(c.value, 0);
      if (sub.kind === "pct") return `${Math.round(c.value)}%`;

      return this.fmt(c.value);
    },
    // Null first: on quota/class/count sub-rows the BAKI columns are
    // structurally absent (only ceiling rows carry a baki at all), so v is
    // null there regardless of unmeasured — that must render blank, not "—".
    // Only once a column can actually hold a baki does the unmeasured rule
    // (never render 0.00) take over.
    rollup(sub, v, unmeasured) {
      if (v == null) return "";
      if (unmeasured) return "—";
      if (sub.kind === "pct") return `${Math.round(v)}%`;
      if (sub.kind === "count") return this.fmt(v, 0);

      return this.fmt(v);
    },
  },
};
</script>

<style lang="scss" scoped>

// Bootstrap absolutely positions .close at top:0 with its own 0.75rem/1.25rem
// padding, which top-aligns the x against this alert's smaller py-2 and looks
// worse still when the message wraps to several lines. Centre it instead.
.alert-dismissible .close {
  top: 50%;
  transform: translateY(-50%);
  padding: 0 0.75rem;
  line-height: 1;
}
.kjp-table {
  font-size: 0.75rem;
  white-space: nowrap;
  // The table reads as a spreadsheet surface: white base, with the
  // workbook's grey/amber accent fills layered on top by the more specific
  // rules below. The surrounding pane, tab strips, warning banner and
  // sidebar are untouched — this dark-on-white swap is scoped to the table.
  background-color: #fff;

  th,
  td {
    background-color: #fff;
    color: var(--kjp-text);
  }

  th {
    vertical-align: middle;
    text-align: center;
    font-size: 0.7rem;
    // Workbook header fill, overriding the plain white base above.
    background-color: var(--kjp-header-fill);
  }

  // The two "baki" (remaining quota) header columns get the workbook's
  // distinct highlight instead of the regular header fill.
  th.kjp-th-baki {
    background-color: var(--kjp-header-baki-fill);
  }

  // The four roll-up headers are long sentences ("BAKI KUOTA BULANAN BELUM
  // DIGUNAPAKAI SEHINGGA SEPTEMBER"), and the table's blanket `nowrap` forced
  // each onto a single line — four columns far wider than the figures they
  // sit above, pushing the month columns off to the left. Let these wrap and
  // hold them to a fixed width; the data cells below stay nowrap.
  th.kjp-th-summary {
    // pre-line, not normal: the labels carry an explicit newline before their
    // unit (see rollupHeaders), and that break is the workbook's own — it puts
    // "(m²)" on its own line. `normal` would collapse it and let the unit wrap
    // wherever the column width happened to fall.
    white-space: pre-line;
    width: 104px;
    min-width: 104px;
    max-width: 104px;
    line-height: 1.25;
    word-break: break-word;
  }

  // Month cells on district rows vs. the JUMLAH KESELURUHAN rows use
  // different fills in the workbook; roll-up columns (to date / annual /
  // baki) are deliberately left unfilled on both, so they keep the table's
  // plain white base and dark text set above.
  td.kjp-month-cell {
    background-color: var(--kjp-month-fill);
  }

  td.kjp-total-month-cell {
    background-color: var(--kjp-total-month-fill);
  }

  // The em dash for an unmeasured cell needs a muted dark tone to stay
  // legible against the workbook's light month fills as well as the plain
  // white base. kjp-cell-unmeasured is only ever applied alongside
  // kjp-month-cell or kjp-total-month-cell (see the template above).
  td.kjp-month-cell.kjp-cell-unmeasured,
  td.kjp-total-month-cell.kjp-cell-unmeasured {
    color: rgba(0, 0, 0, 0.55);
  }

  // Label/quota/roll-up columns on the JUMLAH KESELURUHAN row fall back to
  // the plain white base above; only its month columns get the total shade.
  .kjp-total td {
    font-weight: 600;
  }

  .kjp-total td.kjp-total-month-cell {
    background-color: var(--kjp-total-month-fill);
  }
}

.kjp-table-wrap {
  overflow: auto;
}

// The Reports page imports print.scss (unscoped, via ReportContainer.vue),
// which forces `background-color: transparent !important` on every element
// and `.table td`/`.table th` under @media print — meant for the
// Generate Report flow, which draws its own print-specific backgrounds via
// that same stylesheet. This table renders its white base and the
// workbook's grey/amber accents as plain CSS background-color, so without an
// override here, printing strips every fill from it too.
//
// Scoped to .kjp-table/.kjp-table-wrap only (this component's own classes,
// not shared by anything in the Generate Report flow), so this cannot change
// that flow's print behaviour. The extra class-level specificity Vue's
// scoped-style attribute adds to every rule here is what lets these
// selectors outrank print.scss's `*` and `.table td/.table th` rules
// regardless of which stylesheet loads last.
@media print {
  .kjp-table-wrap {
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }

  .kjp-table {
    background-color: #fff !important;

    // Only background-color needs restoring here — print.scss's `*` rule
    // never touches `color`, so the muted unmeasured-cell tone a few rules
    // below is left alone and still applies on top of this.
    th,
    td {
      background-color: #fff !important;
    }

    th {
      background-color: var(--kjp-header-fill) !important;
    }

    th.kjp-th-baki {
      background-color: var(--kjp-header-baki-fill) !important;
    }

    td.kjp-month-cell {
      background-color: var(--kjp-month-fill) !important;
    }

    td.kjp-total-month-cell,
    .kjp-total td.kjp-total-month-cell {
      background-color: var(--kjp-total-month-fill) !important;
    }
  }
}
</style>
